<template>
  <div>
    <ExpiButton 
      class="mt-1"
      button-style="outline"
      button-color="green"
      :is-disabled="!active"
      :click="orderFastButtonClicked"
      label-id="Jetzt buchen" 
    />

    <Modal 
      :show="warningModalIsOpen" 
      :close="closeWarningModal"
      size="medium"
    >
      <div class="text-center">
        <img
          src="../../../../static/clubbie.svg"
        >
        <div class="mt-3 mb-4">
          <div class="mb-2 fs-16">
            Sie werden zur Buchung weitergeleitet.
          </div>
          <div>
            Die Buchung erfolgt nur über Ihre Agentur, wenn Ihnen die Agenturnummer in der Buchungsstrecke rechts oben bis zum Ende der Buchung angezeigt wird.
          </div>
        </div>

        <Checkbox
          class="mb-3 justify-content-center"
          :clicked="modalCheckboxClicked"
          :label="'Diese Meldung nicht mehr anzeigen.'"
        />

        <div class="d-flex justify-content-center mb-3">
          <ExpiButton
            button-style="outline"
            :click="modalButtonClicked"
            label-id="Fortsetzen"
          />
        </div>
      </div>
    </Modal>
  </div>
</template>

<script lang="ts">

import { Direction } from '@/bewotec/openapi'
import { IBookingFastQuery } from '@/components/interfaces'
import { handleError, handleInfo } from '@/general/errorHandling'
import { isDevSTAGE, isTestSTAGE } from '@/general/helper'
import { useCabinStore, useCruiseStore, useDetailsStore, useFilterStore, useFlexFlightStore, useFlightStore, useRailStore, useUserStore } from '@/store'
import { useAppStore } from '@/store/app'
import { convertIBEPriceModel } from '@/store/details/cruise/helper'
import { RailDirection } from '@/store/details/rail/interfaces'
import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'
import { LocationQueryRaw } from 'vue-router'
import { addAdobeConsent } from '../../../store/tracking/helper'
import Checkbox from '../../common/Checkbox.vue'
import ExpiButton from '../../common/ExpiButton.vue'
import Modal from '../../common/Modal.vue'

export default defineComponent({
  components: { Checkbox, Modal, ExpiButton },

  props: {
    active: { type: Boolean, default: true },
  },

  data: () => {
    return {
      warningModalIsOpen: false,
      link: '',
      hideWarningModalChecked: false,
    }
  },

  computed: {
    ...mapState(useUserStore, {
      leaveWarningArray: store => store.hideExpinetLeaveWarningArray,
      agencyId: store => store.agencyId,
      agentLogin: store => store.agentLogin,
      agentId: store => store.agentId,
      agencyCountry: store => store.agencyCountry,
      expiCode: store => store.expiCode,
    }),
    ...mapState(useFilterStore, {
      paxConfig: store => store.paxConfig
    }),
    ...mapState(useAppStore, {
      bosysReference: store => store.bosysReference 
    }),
    ...mapState(useCabinStore, {
      selectedByUser: store => store.selectedByUser,
    }),
    ...mapState(useFlexFlightStore, {
      birthdates: store => store.birthdates,
      flexFlightFilters: store => store.selectedFilters,
      rnfFlexDirection: store => store.railDirection,
      rnfFlexOption: store => store.selectedRailOption,
    }),
    ...mapState(useDetailsStore, {
      priceModel: store => store.priceModel,
      selectedTransportation: store => store.transportation,
    }),
    ...mapState(useCruiseStore, {
      cruise: store => store.cruise
    }),
    ...mapState(useCabinStore, {
      category: store => store.category,
      cabin: store => store.cabin,
    }),
    ...mapState(useFlightStore, {
      rnfDirection: store => store.railDirectionRNF,
      rnfOption: store => store.selectedRailOption,
      rnfAdults: store => store.railAdults,
      selectedAidaFlight: store => store.selectedFlight
    }),
    ...mapState(useRailStore, {
      railAdults: store => store.railAdults,
      railOption: store => store.selectedRailOption,
      railDirection: store => store.railDirection,
    }),

    buttonDisabled() {
      return !this.active
    },
  },

  methods: {
    ...mapActions(useUserStore, {
      setAddExpinetLeaveWarning: 'setAddExpinetLeaveWarning'
    }),
    
    isShowWarningModal() {
      if (!this.agentId) return true

      if (this.leaveWarningArray.includes(this.agentId)) {
        return false
      }

      return true
    },

    orderFastButtonClicked() {
      if (this.buttonDisabled) {
        return
      }

      if (this.isShowWarningModal()) {
        this.openWarningModal()
      } else {
        this.orderFast()
      }
    },

    openWarningModal() {
      this.warningModalIsOpen = true
    },

    closeWarningModal() {
      this.warningModalIsOpen = false
      this.link = ''
      this.hideWarningModalChecked = false
    },

    modalCheckboxClicked() {
      this.hideWarningModalChecked = !this.hideWarningModalChecked
    },

    async modalButtonClicked() {
      if (this.hideWarningModalChecked) {
        await this.setAddExpinetLeaveWarning(this.agentId!)
      }

      this.orderFast()
      this.closeWarningModal()
    },

    async orderFast() {
      if (!this.cruise) {
        handleError({error: new Error('Cruise is null')})
        return
      }

      const bookingFastQuery: IBookingFastQuery = {
        aidaSource: 'expinet',
        userType: 'agency',
        agencyId: this.agencyId,
        expiCode: this.expiCode,

        adults: this.paxConfig.adults.toString(),
        juveniles: this.paxConfig.juveniles.toString(),
        children: this.paxConfig.children.toString(),
        babies: this.paxConfig.babies.toString(),

        cabinCategory: this.category ? this.category.id : null,
        cabinNumber: this.selectedByUser && this.cabin ? this.cabin.id : undefined,
      }

      if (!!this.bosysReference) {
        bookingFastQuery.reference = this.bosysReference
      }

      if (!!this.agentLogin) {
        bookingFastQuery.agentLogin = encodeURI(this.agentLogin)
      }

      this.createFlightProperties(bookingFastQuery)
      this.createRailObject(bookingFastQuery)

      const countryCode = this.agencyCountry === 'CH' ? 'ch' : 'de'

      const bookingDomain = isDevSTAGE
        ? 'http://localhost:4000'
        : isTestSTAGE
        ? 'https://stage.aida.' + countryCode
        : 'https://aida.' + countryCode

      const bookingRoute = this.$router.resolve({
        path: `buchen/${this.cruise.id}/${convertIBEPriceModel(this.priceModel!)}/meine-reise`,
        query: bookingFastQuery as any as LocationQueryRaw,
      })

      let bookingFastURL = bookingDomain + bookingRoute.href
      bookingFastURL = addAdobeConsent(bookingFastURL)

      window.open(bookingFastURL, '_blank')

      handleInfo('jetzt-buchen', {
        offerUrl: bookingFastURL,
      })
    },

    createFlightProperties(bookingFastQuery: IBookingFastQuery) {
      switch (this.selectedTransportation) {
        case 'Flug':
          bookingFastQuery.flightType = 'air'
          bookingFastQuery.arrAirport = this.selectedAidaFlight?.inboundFlight?.arrival.airportId ?? undefined
          bookingFastQuery.arrAirportClass = this.selectedAidaFlight?.inboundFlight?.mainFlight.flightClass.id ?? undefined
          bookingFastQuery.depAirport = this.selectedAidaFlight?.outboundFlight?.departure.airportId ?? undefined
          bookingFastQuery.depAirportClass = this.selectedAidaFlight?.outboundFlight?.mainFlight.flightClass.id ?? undefined
          break
        case 'Wunschflug':
          bookingFastQuery.flightType = 'flex'

          if (!!this.flexFlightFilters.gateway) {
            bookingFastQuery.airportCode = this.flexFlightFilters.gateway.locCd
          }

          if (!!this.flexFlightFilters.transportationClass) {
            bookingFastQuery.transportationClass = this.flexFlightFilters.transportationClass
          }

          if (!!this.flexFlightFilters.flightDirection) {
            bookingFastQuery.direction = this.flexFlightFilters.flightDirection
          }

          if (this.flexFlightFilters.airlines.length) {
            bookingFastQuery.airlines = this.flexFlightFilters.airlines.toString()
          }

          if (!!this.birthdates) {
            bookingFastQuery.birthdates = Object.values(this.birthdates).toString()
          }

          bookingFastQuery.nonstop = this.flexFlightFilters.nonstop
          break
        default:
          break
      }

      return bookingFastQuery
    },

    createRailObject(bookingFastQuery: IBookingFastQuery) {
      switch (this.selectedTransportation) {
        case 'Bahn':
          if (!!this.railOption && 'id' in this.railOption && !!this.railOption.id) {
            bookingFastQuery.railClass = this.railOption.railClassCode
            bookingFastQuery.railTariff = this.railOption.railTarifCode
            bookingFastQuery.railDirection = this.mapRailDirection(this.railDirection)
            bookingFastQuery.railAdults = this.railAdults
            bookingFastQuery.railType = this.railOption.type
          }
          break
        case 'Flug':
          if (!!this.rnfOption && 'id' in this.rnfOption && !!this.rnfOption.id) {
            bookingFastQuery.railClass = this.rnfOption.railClassCode
            bookingFastQuery.railTariff = this.rnfOption.railTarifCode
            bookingFastQuery.railDirection = this.mapRailDirection(this.rnfDirection)
            bookingFastQuery.railAdults = this.rnfAdults
            bookingFastQuery.railType = this.rnfOption.type
          }
          break
        case 'Wunschflug':
          if (!!this.rnfFlexOption && 'id' in this.rnfFlexOption && !!this.rnfFlexOption.id) {
            bookingFastQuery.railClass = this.rnfFlexOption.railClassCode
            bookingFastQuery.railTariff = this.rnfFlexOption.railTarifCode
            bookingFastQuery.railDirection = this.mapRailDirection(this.rnfFlexDirection)
            bookingFastQuery.railType = this.rnfFlexOption.type
          }
          break
        default:
          break
      }

      return bookingFastQuery
    },

    mapRailDirection(railDirection: RailDirection): Direction | undefined {
      switch (this.selectedTransportation) {
        case 'Flug':
          switch (railDirection) {
            case 'both':
              if (!this.rnfOption || !('id' in this.rnfOption)) {
                handleError({error: new Error('Invalid rnf option')})
                return undefined
              }
              return Direction.RT
            case 'inbound':
              return Direction.R
            case 'outbound':
              return Direction.O
            default:
              return undefined
          }
        case 'Wunschflug':
        case 'Bahn':
          switch (railDirection) {
            case 'both':
              return Direction.RT
            case 'inbound':
              return Direction.R
            case 'outbound':
              return Direction.O
            default:
              return undefined
          }
        default:
          return undefined
      }
    },
  },
})
</script>
